import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Trash2 } from "lucide-react";

const statusStyles = {
  new: "bg-[#C5A059]/15 text-[#8a6c2f] border-[#C5A059]/40",
  contacted: "bg-blue-500/10 text-blue-700 border-blue-300",
  confirmed: "bg-emerald-500/10 text-emerald-700 border-emerald-300",
};

export const EnquiriesTab = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/admin/enquiries").then((res) => setEnquiries(res.data)).catch(() => toast.error("Could not load enquiries"));
  }, []);

  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const visible = filter === "all" ? enquiries : enquiries.filter((e) => e.status === filter);

  return (
    <div data-testid="enquiries-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="font-serif-display text-2xl font-semibold text-[#0A192F]">Guest Enquiries ({enquiries.length})</h2>
        <div className="flex gap-2">
          {["all", "new", "contacted", "confirmed"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs uppercase tracking-wider font-semibold rounded-full px-4 py-2 transition-all ${filter === f ? "bg-[#0A192F] text-[#F8F6F0]" : "bg-white/60 text-[#0A192F]/60 hover:bg-white"}`}
              data-testid={`enquiry-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 && (
        <p className="text-[#4A5568] text-sm bg-white/50 rounded-2xl p-10 text-center" data-testid="enquiries-empty">No enquiries yet. New guest submissions appear here.</p>
      )}

      <div className="space-y-4">
        {visible.map((e) => (
          <div key={e.id} className="bg-[#FAF8F5] border border-[#0D1B2A]/10 rounded-2xl p-6" data-testid={`enquiry-card-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-serif-display text-xl font-semibold text-[#0A192F]">{e.name}</h3>
                  <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded-full px-3 py-1 ${statusStyles[e.status] || statusStyles.new}`} data-testid={`enquiry-status-${e.id}`}>
                    {e.status}
                  </span>
                </div>
                <p className="text-[#4A5568] text-sm mt-2">
                  {e.check_in && e.check_out ? `${e.check_in} → ${e.check_out} · ` : ""}
                  {e.room || "Any room"} · {e.guests} guest{e.guests > 1 ? "s" : ""}
                  {e.email ? ` · ${e.email}` : ""}
                </p>
                {e.message && <p className="text-[#4A5568] text-sm mt-2 italic">“{e.message}”</p>}
                <p className="text-[#0D1B2A]/40 text-xs mt-2">{new Date(e.created_at).toLocaleString()}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <a href={`tel:${e.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 bg-[#0A192F] text-[#F8F6F0] text-xs font-semibold rounded-full px-4 py-2.5 hover:bg-[#C5A059] hover:text-[#0A192F] transition-all" data-testid={`enquiry-call-${e.id}`}>
                  <Phone size={13} /> {e.phone}
                </a>
                {e.status !== "contacted" && (
                  <button onClick={() => setStatus(e.id, "contacted")} className="text-xs font-semibold rounded-full px-4 py-2.5 border border-[#0D1B2A]/20 hover:border-[#C5A059] text-[#0D1B2A]/70 transition-all" data-testid={`enquiry-contacted-${e.id}`}>
                    Contacted
                  </button>
                )}
                {e.status !== "confirmed" && (
                  <button onClick={() => setStatus(e.id, "confirmed")} className="text-xs font-semibold rounded-full px-4 py-2.5 border border-[#0D1B2A]/20 hover:border-emerald-500 text-[#0D1B2A]/70 transition-all" data-testid={`enquiry-confirm-${e.id}`}>
                    Confirm
                  </button>
                )}
                <button onClick={() => remove(e.id)} className="text-red-400 hover:text-red-600 p-2 transition-colors" data-testid={`enquiry-delete-${e.id}`} aria-label="Delete enquiry">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
